
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import PDFCertificatePreview from '@/components/student-certification/certificate-templates/PDFCertificatePreview';
import { CertificateTemplateElement, CertificateTemplate } from '@/types/certificateTemplate';
import { certificateTemplateService } from '@/services/certification/templates';
import { v4 as uuidv4 } from 'uuid';

const CreateTemplatePreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [basicInfo, setBasicInfo] = useState<any>(null);
  const [pdfData, setPdfData] = useState<any>(null);
  const [mappedFields, setMappedFields] = useState<CertificateTemplateElement[]>([]);
  const [previewData, setPreviewData] = useState<Record<string, string>>({
    studentName: 'John Doe',
    courseName: 'Introduction to Programming',
    programTitle: 'Computer Science',
    completionDate: 'June 15, 2025',
    enrollmentDate: 'January 10, 2025',
    grade: 'A',
    instructorName: 'Dr. Jane Smith',
    teacherName: 'Prof. Robert Johnson',
    trainingTitle: 'Advanced Teaching Techniques',
    verificationCode: 'CERT-123456'
  });

  useEffect(() => {
    // Check for basic info
    const storedBasicInfo = sessionStorage.getItem('certificateTemplateBasicInfo');
    if (!storedBasicInfo) {
      toast.error('Please complete the basic information first');
      navigate('/certificate-templates/new/basic-info');
      return;
    }
    setBasicInfo(JSON.parse(storedBasicInfo));
    
    // Load PDF data if available
    const storedPdfData = sessionStorage.getItem('certificateTemplatePdfData');
    if (storedPdfData) {
      setPdfData(JSON.parse(storedPdfData));
    }
    
    // Load field mapping if available
    const storedMapping = sessionStorage.getItem('certificateTemplateMapping');
    if (storedMapping) {
      try {
        setMappedFields(JSON.parse(storedMapping));
      } catch (error) {
        console.error('Error parsing stored mapping:', error);
      }
    }
  }, [navigate]);

  const handlePrevious = () => {
    navigate('/certificate-templates/new/mapping');
  };

  const handleSaveTemplate = () => {
    if (!basicInfo) {
      toast.error('Missing basic information');
      return;
    }
    
    try {
      // Prepare template data
      const templateData: Omit<CertificateTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
        name: basicInfo.name,
        description: basicInfo.description,
        type: basicInfo.type,
        backgroundColor: basicInfo.backgroundColor,
        backgroundImage: basicInfo.backgroundImage,
        isDefault: basicInfo.isDefault,
        size: { width: 800, height: 600 },
        elements: mappedFields,
        createdBy: 'User',
        metadata: pdfData ? {
          isPdf: true,
          pdfUrl: pdfData.url,
        } : undefined
      };
      
      // Save the template
      const createdTemplate = certificateTemplateService.createCertificateTemplate(templateData);
      
      if (createdTemplate) {
        toast.success('Certificate template created successfully');
        
        // Clear session storage
        sessionStorage.removeItem('certificateTemplateBasicInfo');
        sessionStorage.removeItem('certificateTemplatePdfData');
        sessionStorage.removeItem('certificateTemplateMapping');
        
        // Navigate back to templates list
        navigate('/certificate-templates');
      } else {
        toast.error('Failed to create certificate template');
      }
    } catch (error) {
      console.error('Error creating certificate template:', error);
      toast.error('An error occurred while creating the template');
    }
  };

  const handleCancel = () => {
    navigate('/certificate-templates');
  };

  return (
    <div className="container mx-auto py-8">
      <Helmet>
        <title>Create Certificate Template - Preview | NALC</title>
      </Helmet>
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Create Certificate Template</h1>
        <p className="text-muted-foreground mt-2">
          Step 4: Preview and save your certificate template
        </p>
      </div>
      
      <div className="flex justify-center mb-6">
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">Basic Info</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">PDF Upload</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
              ✓
            </div>
            <span className="text-sm mt-1">Field Mapping</span>
          </div>
          <div className="w-12 h-1 bg-primary mx-1"></div>
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
              4
            </div>
            <span className="text-sm mt-1 text-primary font-medium">Preview</span>
          </div>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Certificate Preview</CardTitle>
          <CardDescription>
            Preview how the certificate will look with sample data and save your template
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pdfData && pdfData.url && mappedFields.length > 0 ? (
            <PDFCertificatePreview
              pdfUrl={pdfData.url}
              elements={mappedFields}
              certificateData={previewData}
            />
          ) : pdfData && pdfData.url ? (
            <div>
              <p className="text-amber-500 mb-4">No field mappings defined. The PDF will be used as is.</p>
              <div className="border rounded h-[500px]">
                <iframe
                  src={`${pdfData.url}#toolbar=0`}
                  className="w-full h-full"
                  title="PDF Certificate Template"
                />
              </div>
            </div>
          ) : basicInfo ? (
            <div>
              <p className="text-amber-500 mb-4">No PDF template uploaded. A standard certificate will be created.</p>
              <div className="border rounded h-[500px] flex items-center justify-center"
                style={{
                  backgroundColor: basicInfo.backgroundColor || '#ffffff',
                  backgroundImage: basicInfo.backgroundImage ? `url(${basicInfo.backgroundImage})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="bg-white/90 p-8 rounded shadow-lg text-center max-w-md">
                  <h2 className="text-2xl font-bold mb-4">{basicInfo.name || 'Certificate Template'}</h2>
                  <p className="mb-2">{basicInfo.description || 'This is a preview of the certificate template.'}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No template data available for preview</p>
            </div>
          )}
          
          <div className="flex justify-between mt-8 pt-4 border-t">
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="button" onClick={handlePrevious}>
                <ChevronLeft className="mr-2 h-4 w-4" /> Previous
              </Button>
            </div>
            <Button onClick={handleSaveTemplate}>
              <Save className="mr-2 h-4 w-4" /> Save Template
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateTemplatePreviewPage;
